import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames';
import Lightbox from 'react-images';

import { Icon, Spinner } from '@breezeway/brzw-components';
import CloudinaryImage from '../images/v2/cloudinary_image';
import { getCloudinaryUrl } from '../../utils/image';

import './photo_roll.scss';

const CLASS_NAME = 'photo-roll';

const lightboxTheme = {
  container: { background: 'rgba(0, 0, 0, .6)' },
  lightboxOriginalLink: { color: 'white' },
};

class PhotoRoll extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLightboxOpen: false,
      lightboxIndex: 0,
    };

    this.closeLightbox = this.closeLightbox.bind(this);
    this.goToNextLightboxPhoto = this.goToNextLightboxPhoto.bind(this);
    this.goToPreviousLightboxPhoto = this.goToPreviousLightboxPhoto.bind(this);
    this.onClickPhoto = this.onClickPhoto.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.renderPhoto = this.renderPhoto.bind(this);
    this.renderPhotoWrapper = this.renderPhotoWrapper.bind(this);
  }

  onClickPhoto(photo, index) {
    return () => {
      const { withLightbox, onClickEachPhoto } = this.props;
      const photoKey = _.get(photo, 'photo_key');

      if (onClickEachPhoto) onClickEachPhoto(photo, index);

      if (withLightbox && photoKey) this.setState({ isLightboxOpen: true, lightboxIndex: index });
    };
  }

  onDelete(photoId) {
    return e => {
      const { onDelete } = this.props;
      _.invoke(e, 'preventDefault');
      _.invoke(e, 'stopPropagation');
      onDelete(photoId);
    };
  }

  closeLightbox() {
    this.setState({ isLightboxOpen: false });
  }

  goToPreviousLightboxPhoto() {
    this.setState(prevState => ({ lightboxIndex: prevState.lightboxIndex - 1 }));
  }

  goToNextLightboxPhoto() {
    this.setState(prevState => ({ lightboxIndex: prevState.lightboxIndex + 1 }));
  }

  renderPhotoWrapper(photo, index) {
    const { editable, withLightbox } = this.props;
    const { id, isLoading } = photo;

    return (
      <div
        className={classnames(`${CLASS_NAME}__photo-wrapper`, {
          [`${CLASS_NAME}__photo-wrapper--is-loading`]: isLoading,
          [`${CLASS_NAME}__photo-wrapper--with-lightbox`]: withLightbox,
        })}
        key={id}
        onClick={this.onClickPhoto(photo, index)}
        role="presentation"
      >
        {this.renderPhoto(photo)}
        {isLoading && (
          <div className={`${CLASS_NAME}__spinner-wrapper`}>
            <Spinner />
          </div>
        )}
        {!isLoading && editable && (
          <div role="presentation" className={`${CLASS_NAME}__remove`} onClick={this.onDelete(id)}>
            <Icon iconName="close" className={`${CLASS_NAME}__remove-icon`} />
          </div>
        )}
      </div>
    );
  }

  renderPhoto(photo) {
    const { thumbSize } = this.props;
    const { src } = photo;
    const photoKey = _.get(photo, 'photo_key');

    if (photoKey) {
      const cloudPhotoStyle = {
        width: thumbSize,
        height: thumbSize,
      };

      return (
        <CloudinaryImage
          bucket={photo.bucket}
          className={`${CLASS_NAME}__image`}
          options={{
            ...cloudPhotoStyle,
            crop: 'fill',
          }}
          photoKey={photo.photo_key}
          withLoader
        />
      );
    }

    if (src) {
      return (
        <img alt="" className={`${CLASS_NAME}__image ${CLASS_NAME}__image--preview`} src={src} />
      );
    }

    return null;
  }

  renderLightbox() {
    const { lightboxPhotoHeight, lightboxPhotoWidth, photos, withLightbox } = this.props;
    const lightboxPhotos = _.filter(photos, photo => !photo.isLoading);
    const { lightboxIndex, isLightboxOpen } = this.state;

    return (
      withLightbox && (
        <Lightbox
          currentImage={lightboxIndex}
          images={_.map(lightboxPhotos, ({ bucket, photo_key: key }) => ({
            src: getCloudinaryUrl(`${bucket}/${key}`, {
              width: lightboxPhotoWidth,
              height: lightboxPhotoHeight,
              crop: 'limit',
            }),
            caption: (
              <a
                className="lightbox-original-link"
                href={`https://s3.amazonaws.com/${bucket}/original/${key}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                Download original
              </a>
            ),
          }))}
          isOpen={isLightboxOpen}
          onClickNext={this.goToNextLightboxPhoto}
          onClickPrev={this.goToPreviousLightboxPhoto}
          onClose={this.closeLightbox}
          theme={lightboxTheme}
        />
      )
    );
  }

  render() {
    const { className, editable, photos } = this.props;

    return (
      <div
        className={classnames(CLASS_NAME, className, {
          [`${CLASS_NAME}--editable`]: editable,
        })}
      >
        {_.map(photos, this.renderPhotoWrapper)}
        {this.renderLightbox()}
      </div>
    );
  }
}

PhotoRoll.propTypes = {
  className: PropTypes.string,
  editable: PropTypes.bool,
  lightboxPhotoHeight: PropTypes.number,
  lightboxPhotoWidth: PropTypes.number,
  onDelete: PropTypes.func,
  onClickEachPhoto: PropTypes.func,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      src: PropTypes.string,
    }),
  ),
  thumbSize: PropTypes.number,
  withLightbox: PropTypes.bool,
};

PhotoRoll.defaultProps = {
  lightboxPhotoHeight: 480,
  lightboxPhotoWidth: 720,
  thumbSize: 88,
  withLightbox: false,
};

export default PhotoRoll;
